import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { ICarnet } from "Dashboard/scenes/Carnets/domain/ICarnet";
import { DiffDays, FormatDateShort, GetIsoDateTimeStamp } from "utils/dateUtils";
import { MealType } from "Dashboard/scenes/Carnets/domain/MealType";
import { AbsenceType } from "Dashboard/scenes/Carnets/domain/AbsenceType";
import {
  setDeleteCarnetModalOpen,
  setNoteForCarnetPopover
} from "../carnets.slice";
import { useAppDispatch } from "hooks/hooks";
import InfoTooltip from "components/Popup/InfoTooltip";
import { FormatNumberOrNull } from "utils/numberUtils";
import { ICarentsTotals } from "Dashboard/scenes/Carnets/domain/ICarnetsTotals";
import { selectState as selectStateAuth } from "authentication/main.slice";
import { hasFullAccessTo, hasWriteAccessTo } from "utils/authUtil";
import { CARNET_MODULE_NAME } from "Dashboard/scenes/Carnets/domain/CarnetModule";
import { useSelector } from "react-redux";

interface IProps {
  carnet: ICarnet;
  totals: ICarentsTotals;
  index: number;
}

const CarnetsRow = ({ carnet, totals, index }: IProps) => {
  const dispatch = useAppDispatch();
  const { user } = useSelector(selectStateAuth);
  const carnetValueFormat = new Intl.NumberFormat("sr-SR", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  const isMonday = new Date().getDay() === 1;
  const carnetDateDiffDays = DiffDays(carnet.carnetDate);
  const hasDeleteOption = hasFullAccessTo(user, CARNET_MODULE_NAME)
    || (hasWriteAccessTo(user, CARNET_MODULE_NAME)
      && carnetDateDiffDays <= 0 && (isMonday ? carnetDateDiffDays >= -2 : carnetDateDiffDays >= -1)
      && user?.employee?.employeeId === carnet.createdBy.employeeId);

  return (
    <div
      className="row"
      style={{ "--carnet-hour-columns": totals.hoursTotals.length } as React.CSSProperties}
    >
      {hasDeleteOption ? (
        <InfoTooltip tooltip="Obriši zapis" classesProp="col center">
          <button
            className="del-button"
            onClick={(e) => {
              e.stopPropagation();
              dispatch(setDeleteCarnetModalOpen({ visible: true, index }));
            }}
          >
            <HighlightOffIcon className="fas" fontSize="small" />
          </button>
        </InfoTooltip>
      ) : <div></div>}
      <div className="col center">
        <div
          className={
            carnet.absenceType === AbsenceType.VACATION
              ? "table-name-active"
              : "bg-item"
          }
        >
          {carnet.employee.employeeName}
        </div>
      </div>
      <div className="col center">
        <div className="bg-item center strong">
          {FormatDateShort(carnet.carnetDate)}
        </div>
      </div>
      {totals.hoursTotals.map((hoursTotal, index) =>
        <div className="col center" key={index}>
          <input
            type="text"
            className="input-center no-clickable"
            value={FormatNumberOrNull(carnet.hourlyWorkItems?.[hoursTotal.index], carnetValueFormat) ?? ""}
            readOnly
          />
        </div>
      )}
      <div className="col space-box">
        <InfoTooltip tooltip="Doručak" element="span" classesProp="checkbox-container">
          <input
            className="no-clickable"
            type="checkbox"
            checked={carnet.breakfast === MealType.REGULAR}
            readOnly
          />
        </InfoTooltip>
        <InfoTooltip tooltip="Ručak" element="span" classesProp="checkbox-container">
          <input
            className="no-clickable"
            type="checkbox"
            checked={carnet.lunch === MealType.REGULAR}
            readOnly
          />
        </InfoTooltip>
        <InfoTooltip tooltip="Večera" element="span" classesProp="checkbox-container">
          <input
            className="no-clickable"
            type="checkbox"
            checked={carnet.dinner === MealType.REGULAR}
            readOnly
          />
        </InfoTooltip>
      </div>
      <div className="col space-box">
        <InfoTooltip tooltip="Doručak" element="span" classesProp="checkbox-container">
          <input
            className="no-clickable"
            type="checkbox"
            checked={carnet.breakfast === MealType.CASH}
            readOnly
          />
        </InfoTooltip>
        <InfoTooltip tooltip="Ručak" element="span" classesProp="checkbox-container">
          <input
            className="no-clickable"
            type="checkbox"
            checked={carnet.lunch === MealType.CASH}
            readOnly
          />
        </InfoTooltip>
        <InfoTooltip tooltip="Večera" element="span" classesProp="checkbox-container">
          <input
            className="no-clickable"
            type="checkbox"
            checked={carnet.dinner === MealType.CASH}
            readOnly
          />
        </InfoTooltip>
      </div>
      <div className="col center">
        <span className="checkbox-container">
          <input
            className="no-clickable"
            type="checkbox"
            checked={carnet.absenceType === AbsenceType.SICK_LEAVE}
            readOnly
          />
        </span>
      </div>
      <div className="col center">
        <span className="checkbox-container">
          <input
            className="no-clickable red-checkmark"
            type="checkbox"
            checked={carnet.absenceType === AbsenceType.VACATION}
            readOnly
          />
        </span>
      </div>
      <div className="col">
        <div className="bg-item overflowMinWidth">
          <InfoTooltip tooltip={carnet.project?.projectName}
            classesProp="bg-text-overflow">
            {carnet.project?.projectName}
          </InfoTooltip>
        </div>
      </div>
      <div className="col created-by left">
        <div className="bg-item bg-item-border-light remove-border overflowMinWidth padding-x0">
          <InfoTooltip
            tooltip={`${carnet.createdBy.name} ${GetIsoDateTimeStamp(carnet.createdAt)}`}
            element="span"
            classesProp="bg-text-overflow strong"
          >
            {carnet.createdBy.name}{" "}
            {GetIsoDateTimeStamp(carnet.createdAt)}
          </InfoTooltip>
        </div>
        <button
          onClick={(e) => {
            dispatch(setNoteForCarnetPopover({
              popoverState: { isOpen: true, top: e.clientY, left: e.clientX },
              index: index
            }));
          }}
        >
          <HelpOutlineIcon className="far" />
        </button>
      </div>
    </div>
  );
};

export default CarnetsRow;
